import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, Check, Clock } from "lucide-react";
import { client, BOOKING_URL, NAP } from "../lib/config";
import PageHero from "../components/PageHero";
import Breadcrumbs from "../components/Breadcrumbs";
import ReviewsStrip from "../components/ReviewsStrip";
import BookCallCTA from "../components/BookCallCTA";
import RoomCard from "../components/RoomCard";
import RoomDialog from "../components/RoomDialog";

/**
 * Reusable audience landing page.
 * config: { eyebrow, title, intro, image, metaTitle, metaDesc,
 *           breadcrumbLabel, whyBullets:[{title,body}],
 *           amenityHighlights:[string], roomSlugs:[string],
 *           faqCategory or faqQuestions:[{q,a}],
 *           blogSlugs:[string], keywordCluster:[string] }
 */
export default function AudiencePage({ config }) {
  const [allRooms, setAllRooms] = useState([]);
  const [posts, setPosts] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [ramps, setRamps] = useState([]);
  const [openRoom, setOpenRoom] = useState(null);

  useEffect(() => {
    client.get("/rooms").then((r) => setAllRooms(r.data)).catch(() => {});
    if (config.faqCategory) {
      client.get(`/faqs?category=${encodeURIComponent(config.faqCategory)}`).then((r) => setFaqs(r.data)).catch(() => {});
    }
    client.get("/blog-posts").then((r) => setPosts(r.data)).catch(() => {});
    if (config.showTrumanRamps) {
      client.get("/truman-ramps").then((r) => setRamps(r.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.faqCategory, config.showTrumanRamps]);

  const rooms = allRooms.filter((r) => config.roomSlugs.includes(r.slug));
  const relatedPosts = posts.filter((p) => config.blogSlugs.includes(p.slug));

  return (
    <>
      <PageHero
        eyebrow={config.eyebrow}
        title={config.title}
        intro={config.intro}
        image={config.image}
        metaTitle={config.metaTitle}
        metaDesc={config.metaDesc}
      />
      <Breadcrumbs items={[{ label: config.breadcrumbLabel || config.eyebrow }]} />

      {/* Why us */}
      <section className="bg-[#FDFBF7]">
        <div className="wb-container py-16 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="wb-eyebrow">Why Westbridge for this trip</div>
            <h2 className="wb-h2 mt-3">{config.whyTitle || "Built around the reason you're coming."}</h2>
            {config.whyLead && <p className="wb-body mt-4">{config.whyLead}</p>}
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="wb-btn-primary" data-testid="audience-book">
                Book Now <ArrowRight className="h-4 w-4" />
              </a>
              <a href={`tel:${NAP.phoneTel}`} className="wb-btn-outline" data-testid="audience-call">
                <Phone className="h-4 w-4" /> {NAP.phone}
              </a>
            </div>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {config.whyBullets.map((b) => (
              <div key={b.title} className="wb-card p-5 bg-white" data-testid={`why-${b.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <h3 className="text-base font-black text-[#1A1A1A]" style={{ fontFamily: "'Nunito', sans-serif" }}>{b.title}</h3>
                <p className="wb-body text-sm mt-2">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Amenity highlights */}
      {config.amenityHighlights && config.amenityHighlights.length > 0 && (
        <section className="bg-[#F5F0E6] border-y border-[#E5DFD3]">
          <div className="wb-container py-12 md:py-16">
            <div className="wb-eyebrow mb-6">Amenities that matter for this trip</div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {config.amenityHighlights.map((a) => (
                <li key={a} className="flex items-start gap-2 text-sm text-[#1A1A1A]">
                  <Check className="h-4 w-4 text-[#B94A15] mt-0.5 flex-shrink-0" />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Truman Lake ramps (boaters page) */}
      {config.showTrumanRamps && ramps.length > 0 && (
        <section className="bg-[#1A1A1A] text-white" data-testid="audience-truman-ramps">
          <div className="wb-container py-16 md:py-20">
            <div className="max-w-2xl mb-10">
              <div className="wb-eyebrow text-[#FF9666]">Truman Lake ramps & marinas</div>
              <h2 className="wb-h2 text-white mt-3">Every ramp within 40 minutes of the front desk.</h2>
              <p className="wb-body text-white/85 mt-4">
                The Grand River arm runs up toward Clinton. These are the three ramps we send guests to — closest first.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {ramps.map((r) => (
                <article
                  key={r.name}
                  className="p-6 border border-white/15 bg-white/5 rounded-sm"
                  data-testid={`audience-ramp-${r.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <h3 className="text-xl font-black text-white" style={{ fontFamily: "'Nunito', sans-serif" }}>{r.name}</h3>
                  <div className="mt-3 flex items-center gap-2 text-sm text-[#FF9666]">
                    <Clock className="h-4 w-4" />
                    <span className="font-bold">~{r.from_hotel_min} min from Westbridge</span>
                  </div>
                  <p className="text-sm text-white/85 mt-4 leading-relaxed">{r.detail}</p>
                </article>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/local-guide#truman-ramps" className="wb-btn-outline border-[#FF9666] text-[#FF9666] hover:bg-white/10" data-testid="audience-ramps-guide-link">
                Full local guide <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Recommended rooms */}
      <section className="bg-[#FDFBF7]">
        <div className="wb-container py-16 md:py-20">
          <div className="max-w-2xl mb-10">
            <div className="wb-eyebrow">Recommended rooms</div>
            <h2 className="wb-h2 mt-3">What to book for this trip.</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {rooms.map((r) => (
              <RoomCard key={r.slug} room={r} onOpen={setOpenRoom} />
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      {faqs.length > 0 && (
        <section className="bg-[#F5F0E6] border-y border-[#E5DFD3]">
          <div className="wb-container py-16 md:py-20">
            <div className="max-w-2xl mb-8">
              <div className="wb-eyebrow">Questions we hear on this trip</div>
              <h2 className="wb-h2 mt-3">{config.faqTitle || "Mini-FAQ."}</h2>
            </div>
            <div className="space-y-3 max-w-4xl">
              {faqs.slice(0, config.faqLimit || 6).map((f) => (
                <details key={f.id} className="wb-card bg-white p-5 group" data-testid={`audience-faq-${f.id}`}>
                  <summary className="cursor-pointer font-bold text-[#1A1A1A] flex justify-between items-center" style={{ fontFamily: "'Nunito', sans-serif" }}>
                    {f.question}
                    <span className="text-[#B94A15] ml-4">+</span>
                  </summary>
                  <p className="wb-body text-base mt-3">{f.answer}</p>
                </details>
              ))}
            </div>
            <div className="mt-6">
              <Link to="/faq" className="wb-btn-outline" data-testid="audience-full-faq">
                See the full FAQ <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      <ReviewsStrip compact />

      {/* Related posts */}
      {relatedPosts.length > 0 && (
        <section className="bg-[#FDFBF7]">
          <div className="wb-container py-16 md:py-20">
            <div className="max-w-2xl mb-10">
              <div className="wb-eyebrow">Related reading from the journal</div>
              <h2 className="wb-h2 mt-3">Dig deeper before you book.</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="wb-card bg-white group flex flex-col" data-testid={`audience-post-${p.slug}`}>
                  {p.hero && !p.hero.includes("/area-") ? (
                    <div className="aspect-[4/3] overflow-hidden bg-[#F5F0E6]">
                      <img src={p.hero} alt="" aria-hidden="true" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    </div>
                  ) : (
                    <div className="aspect-[4/3] relative overflow-hidden bg-[#1A1A1A]">
                      <div className="absolute inset-0 bg-gradient-to-br from-[#B94A15]/70 via-[#1A1A1A] to-[#1A1A1A]" aria-hidden="true" />
                      <div aria-hidden="true" className="absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(60% 55% at 22% 30%, rgba(232,93,38,0.55) 0%, rgba(232,93,38,0) 60%)" }} />
                      <div className="relative h-full flex flex-col justify-end p-5">
                        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-[#FF9666]" style={{ fontFamily: "'Nunito', sans-serif" }}>{p.category}</div>
                      </div>
                    </div>
                  )}
                  <div className="p-5 flex-1">
                    <div className="wb-eyebrow">{p.category}</div>
                    <h3 className="text-lg font-black text-[#1A1A1A] mt-2 group-hover:text-[#B94A15]" style={{ fontFamily: "'Nunito', sans-serif" }}>
                      {p.title}
                    </h3>
                    <p className="wb-body text-sm mt-2">{p.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <BookCallCTA title={config.ctaTitle || "Book direct. Talk to a real person. Sleep well."} />

      <RoomDialog room={openRoom} open={!!openRoom} onOpenChange={(v) => !v && setOpenRoom(null)} />
    </>
  );
}
